/* Copyright (c) 2017 Backpack Health LLC */
import React from 'react';

import { authToken, sessionstore, StorageKeys } from '../../../utilities/LocalStorage';

import SessionService from '../../../apiServices/SessionService';
import AppUtils from '../../../utilities/AppUtils';
import ContentLoader from '../../components/ContentLoader';
import { useUIState } from '../../hooks/useUIState';
import { useLists } from '../../hooks/useLists';
import { useUserActionItems } from '../../hooks/useActionItems';
import { useActiveUser } from '../../hooks/useUser';

const AuthenticatedApp = React.lazy(() => import('./AuthenticatedApp'));
const UnAuthenticatedApp = React.lazy(() => import('./UnAuthenticatedApp'));

interface Props {
  store: any;
}
const ApplicationFrame: React.FC<Props> = (props) => {
  const [session, setSession] = React.useState(null);
  const [isLoading, setisLoading] = React.useState(true);
  const [, uiActions] = useUIState();
  const [{ isLoaded }, listActions] = useLists();
  const [{ user }, userActions] = useActiveUser();
  React.useEffect(() => {
    const sessionToken = authToken.get();
    if (AppUtils.hasSessionToken()) {
      SessionService.get(sessionToken, (apiError, session) => {
        if (apiError) {
          setSession(null);
          AppUtils.logout({});
        } else if (session.requiresMfa()) {
          sessionstore.set(StorageKeys.TOKEN_TYPE, session.tokenType());
        } else {
          setSession(session);
          uiActions.setUser(session.userData().id, session.userData().profile_id);
        }
        setisLoading(false);
      });
    } else {
      AppUtils.clearSavedData({ retainMetrics: true });
      setisLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!isLoaded) { return; }

    const locale = listActions.getClosestMatchingLocale();
    userActions.setClientLocale(locale);
  }, [isLoaded]);

  if (isLoading || !isLoaded) { return null; }

  return Boolean(session) ? (
    <React.Suspense fallback={<ContentLoader isContentLoaded={false} isFixed />}>
      <AuthenticatedApp userData={session.userData()} store={props.store} />
    </React.Suspense>
  ) : (
    <React.Suspense fallback={<ContentLoader isContentLoaded={false} isFixed />}>
      <UnAuthenticatedApp store={props.store} />
    </React.Suspense>
  );
};

export default ApplicationFrame;
